import { NumberInput, SegmentedControl, Text, Checkbox } from '@mantine/core';
import { IconArrowDown, IconArrowLeft, IconArrowRight, IconArrowUp } from '@tabler/icons-react';
import React from 'react';
import type { z } from 'zod';
import type { ScrollDirection, zScrollOperationValue } from '~/common/recorder/recorder-operation';
import { isScrollDirection } from '~/common/recorder/recorder-operation';
import { cn } from '~/common/string/classname';

export const ScrollInput = ({
  onDirectionSelect,
  onAmountChange,
  onIsPageLevelScroll,
  label,
  className,
  scrollOperationValue,
  isPageLevelScroll,
}: {
  onDirectionSelect?: (direction: ScrollDirection) => void;
  onAmountChange?: (amount: number) => void;
  onIsPageLevelScroll?: (isPageLevel: boolean) => void;
  label?: string;
  className?: string;
  scrollOperationValue: z.output<typeof zScrollOperationValue>;
  isPageLevelScroll?: boolean;
}) => {
  const handleDirectionChange = (selectedDirection: string) => {
    if (isScrollDirection(selectedDirection)) {
      onDirectionSelect?.(selectedDirection);
    }
  };

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {!!label && <Text size="sm">{label}</Text>}
      <SegmentedControl
        value={scrollOperationValue.direction}
        onChange={handleDirectionChange}
        data={[
          {
            value: 'up' satisfies ScrollDirection,
            label: (
              <div className="flex items-center gap-1">
                <IconArrowUp size={16} />
                <span>Up</span>
              </div>
            ),
          },
          {
            value: 'down' satisfies ScrollDirection,
            label: (
              <div className="flex items-center gap-1">
                <IconArrowDown size={16} />
                <span>Down</span>
              </div>
            ),
          },
          {
            value: 'left' satisfies ScrollDirection,
            label: (
              <div className="flex items-center gap-1">
                <IconArrowLeft size={16} />
                <span>Left</span>
              </div>
            ),
          },
          {
            value: 'right' satisfies ScrollDirection,
            label: (
              <div className="flex items-center gap-1">
                <IconArrowRight size={16} />
                <span>Right</span>
              </div>
            ),
          },
        ]}
      />
      <NumberInput
        label="Amount (pixels)"
        placeholder="100"
        allowNegative={false}
        defaultValue={scrollOperationValue.amount}
        onChange={(val) => onAmountChange?.(Number(val) || 100)}
      />
      <Checkbox
        label="Page-level scroll"
        checked={isPageLevelScroll || false}
        onChange={(event) => onIsPageLevelScroll?.(event.currentTarget.checked)}
      />
    </div>
  );
};

ScrollInput.displayName = 'ScrollInput';
